/**
 * `<page-diff>` — what a publish will change, routed at `/instances/:id/diff`
 * with `?from=` and `?to=` carrying the two refs (`draft` | `live` | a
 * generation number), so a diff link is shareable.
 *
 * Implements `SC-F603` (a unified line-level diff over the deterministically
 * rendered bytes, with added / removed / changed counts, **every hunk labelled
 * with the kind and name of the object it belongs to**, and the rendered server
 * configuration as its own clearly-labelled section because it has a different
 * blast radius), `SC-F607` (the classification of publishing `to` over `from`,
 * stated in the server's own words) and `SC-F613` (the guarded half of
 * `server.yaml` is visible in the diff, redacted, and `admin.token` appears as
 * "rotated" rather than as either value).
 *
 * Stories: `SC-S601`, `SC-S603`, `SC-S607`.
 *
 * A line diff is sufficient here for exactly one reason: both sides come from the
 * same deterministic serializer, so there is no formatting noise to filter. If
 * that invariant is ever relaxed, this screen is the first thing that has to be
 * revisited.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import { publishClassVariant, type BnKvRow, type BnSelectChangeDetail, type BnSelectOption } from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  getDiff,
  listGenerations,
  CHANGE_CLASS_LABELS,
  type DiffFile,
  type DiffHunk,
  type DiffRef,
  type Generation,
  type GenerationDiff,
} from "../api/generations.js";
import { hrefFor, navigate } from "../nav.js";

/** SERVER_CONFIG_PATH is the rendered server configuration's bundle path. It gets
 * its own section: it is the one artifact in the bundle whose change is always
 * restart-class. */
const SERVER_CONFIG_PATH = "server.yaml";

@customElement("page-diff")
export class PageDiff extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  /** The two refs, read from the query string — the router matches on pathname
   * only, so the page owns its own parameters. */
  @state() private from: DiffRef = "live";
  @state() private to: DiffRef = "draft";

  @state() private diff: GenerationDiff | null = null;
  @state() private generations: Generation[] = [];
  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  /** Which changed file is expanded. One at a time, because a diff of twelve files
   * is a scroll and not a reading. */
  @state() private open: Record<string, boolean> = {};

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 74ch;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 74ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .picker {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-lg);
    }
    .picker bn-field {
      flex: 0 1 15rem;
      margin-bottom: 0;
    }
    .counts {
      display: flex;
      gap: var(--bino-space-md);
      flex-wrap: wrap;
      font-size: var(--bino-font-size-md);
    }
    .count strong {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-lg);
      display: block;
      color: var(--bino-text);
    }
    .count span {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .summary {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .summary li {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) 0;
      border-bottom: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-md);
    }
    .summary li:last-child {
      border-bottom: none;
    }
    .op {
      font-family: var(--bino-font-mono);
      font-weight: 600;
      width: 1.25rem;
      text-align: center;
      flex: 0 0 auto;
    }
    .op.added {
      color: var(--bino-diff-add-text);
    }
    .op.removed {
      color: var(--bino-diff-del-text);
    }
    .obj {
      font-weight: 600;
    }
    .path {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .lines {
      margin-left: auto;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      white-space: nowrap;
    }
    .lines .plus {
      color: var(--bino-diff-add-text);
    }
    .lines .minus {
      color: var(--bino-diff-del-text);
    }

    /* The file diff. It scrolls inside its own container: a rendered YAML line can
       be long, and the page body must never scroll horizontally (SC-S602). */
    .file + .file {
      margin-top: var(--bino-space-lg);
    }
    .file-head {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-xs);
    }
    .hunk {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-sm);
      overflow: hidden;
      background: var(--bino-code-bg);
    }
    .hunk + .hunk {
      margin-top: var(--bino-space-sm);
    }
    .hunk-label {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      background: var(--bino-code-gutter-bg);
      border-bottom: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .hunk-label .who {
      font-weight: 600;
      color: var(--bino-text);
    }
    .hunk-label .range {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
    }
    .rows {
      overflow-x: auto;
    }
    table.diff {
      border-collapse: collapse;
      width: 100%;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    table.diff td {
      padding: 0 var(--bino-space-xs);
      vertical-align: top;
      white-space: pre;
    }
    td.n {
      width: 3.5rem;
      text-align: right;
      color: var(--bino-diff-gutter);
      user-select: none;
      background: var(--bino-code-gutter-bg);
    }
    td.mark {
      width: 1rem;
      text-align: center;
      color: var(--bino-diff-gutter);
      user-select: none;
    }
    tr.added td {
      background: var(--bino-diff-add-bg);
      color: var(--bino-diff-add-text);
    }
    tr.removed td {
      background: var(--bino-diff-del-bg);
      color: var(--bino-diff-del-text);
    }
    tr.context td.text {
      color: var(--bino-code-text);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .failure .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // The refs are query parameters rather than properties — the router matches on
    // pathname only — so they are read here, before the first update reads them.
    this.readRefs();
  }

  /** Driven from `willUpdate` alone: the router sets `instanceId` before the first
   * update, so loading from `connectedCallback` too would compute the diff twice. */
  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has("instanceId") && this.instanceId) void this.load();
  }

  /** readRefs takes the two refs from the query string. Unparseable values fall
   * back to the pair a reader almost always wants — "what will change" — rather
   * than to an error, because a mistyped link should still show something true. */
  private readRefs() {
    const params = new URLSearchParams(location.search);
    this.from = parseRef(params.get("from")) ?? "live";
    this.to = parseRef(params.get("to")) ?? "draft";
  }

  private async load() {
    this.loading = true;
    try {
      const [diff, history] = await Promise.all([
        getDiff(this.instanceId, this.from, this.to),
        listGenerations(this.instanceId).catch(() => ({ items: [] as Generation[], next: null })),
      ]);
      this.diff = diff;
      this.generations = history.items;
      this.failure = null;
      // The first changed file is expanded, because a diff whose every section is
      // collapsed makes the reader click before it says anything.
      this.open = diff.files.length > 0 ? { [diff.files[0]!.path]: true } : {};
    } catch (err) {
      this.failure = parseError(err);
      this.diff = null;
      // The history is still worth having: it is what the fallback links point at.
      try {
        const history = await listGenerations(this.instanceId);
        this.generations = history.items;
      } catch {
        this.generations = [];
      }
    } finally {
      this.loading = false;
    }
  }

  private setRef(which: "from" | "to", raw: string) {
    const ref = parseRef(raw) ?? "live";
    if (which === "from") this.from = ref;
    else this.to = ref;
    const url = new URL(location.href);
    url.searchParams.set("from", String(this.from));
    url.searchParams.set("to", String(this.to));
    // Replace rather than push: choosing a comparison is a view of this route, not
    // a route of its own, and it should not cost a back-button press to undo.
    navigate(url.pathname + url.search, { replace: true });
    void this.load();
  }

  private refOptions(): BnSelectOption[] {
    const options: BnSelectOption[] = [
      { value: "draft", label: "draft", description: "The working set — what publishing would ship." },
      { value: "live", label: "live", description: "The generation the instance is serving now." },
    ];
    for (const g of this.generations) {
      options.push({
        value: String(g.generation),
        label: `generation ${g.generation}`,
        description: [
          g.state,
          g.message ? `“${g.message}”` : "",
          g.created_by_label ? `by ${g.created_by_label}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }
    return options;
  }

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderPicker() {
    return html`<div class="picker">
      <bn-field label="From" hint="The older side.">
        <bn-select
          .options=${this.refOptions()}
          .value=${String(this.from)}
          filterable
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setRef("from", e.detail.value)}
        ></bn-select>
      </bn-field>
      <bn-field label="To" hint="The newer side.">
        <bn-select
          .options=${this.refOptions()}
          .value=${String(this.to)}
          filterable
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.setRef("to", e.detail.value)}
        ></bn-select>
      </bn-field>
      <bn-button ?loading=${this.loading} @click=${() => void this.load()}>Refresh</bn-button>
    </div>`;
  }

  /** The structural summary (`SC-F603`): the half most readers read. It is what the
   * API computed, not something re-derived from the text — a browser re-parsing the
   * YAML would be a second implementation of the renderer's own knowledge. */
  private renderSummary(diff: GenerationDiff) {
    return html`<bn-card heading="What changes">
      <div class="counts">
        <span class="count"><strong>${diff.added}</strong><span>objects added</span></span>
        <span class="count"><strong>${diff.changed}</strong><span>objects changed</span></span>
        <span class="count"><strong>${diff.removed}</strong><span>objects removed</span></span>
        <span class="count"
          ><strong>+${diff.lines_added} / −${diff.lines_removed}</strong><span>lines</span></span
        >
      </div>
      ${diff.objects.length > 0
        ? html`<ul class="summary" style="margin-top: var(--bino-space-md)">
            ${diff.objects.map(
              (o) => html`<li>
                <span class="op ${o.change === "added" ? "added" : o.change === "removed" ? "removed" : ""}">
                  ${o.change === "added" ? "+" : o.change === "removed" ? "−" : "~"}
                </span>
                <span class="obj">${o.kind}/${o.name}</span>
                <span class="path">${o.path}</span>
                <span class="lines">
                  <span class="plus">+${o.added}</span> <span class="minus">−${o.removed}</span>
                </span>
              </li>`,
            )}
          </ul>`
        : nothing}
      ${diff.admin_token_rotated
        ? html`<p class="copy" style="margin-top: var(--bino-space-md)">
            <strong>~ admin.token (rotated)</strong> — the instance's admin credential changed. Every
            read surface serves the redacted text, in which the token is a fixed placeholder on both
            sides, so the rotation produces no line of its own. The fact is shown; neither value ever
            is.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /** The classification (`SC-F607`), in the server's own words. */
  private renderVerdict(diff: GenerationDiff) {
    const verdict = diff.verdict;
    const changeClass = verdict?.change_class ?? diff.change_class;
    if (!changeClass) return nothing;
    const restarts = verdict?.restarts ?? false;
    return html`<bn-panel eyebrow="Publishing this">
      <div style="display:flex; gap: var(--bino-space-sm); align-items:center; flex-wrap:wrap">
        <bn-badge .variant=${publishClassVariant(verdict?.publish_path ?? "")}>
          ${restarts ? "restart" : "no restart"}
        </bn-badge>
        <span class="meta">${CHANGE_CLASS_LABELS[changeClass] ?? changeClass}</span>
      </div>
      ${verdict?.blast_radius
        ? html`<p class="copy" style="margin-top: var(--bino-space-sm)">
            <strong>${verdict.blast_radius}</strong>
          </p>`
        : nothing}
      ${verdict?.query_loss_warning
        ? html`<p class="copy">${verdict.query_loss_warning}</p>`
        : nothing}
      ${verdict?.reason ? html`<p class="copy">${verdict.reason}</p>` : nothing}
    </bn-panel>`;
  }

  /** One hunk, labelled with the object it belongs to — which is `SC-S603`'s second
   * criterion and the reason the renderer diffs document by document rather than
   * file by file. */
  private renderHunk(hunk: DiffHunk) {
    return html`<div class="hunk">
      <div class="hunk-label">
        <span class="who">${hunk.label || "(header)"}</span>
        ${hunk.kind ? html`<span class="path">${hunk.kind}${hunk.name ? `/${hunk.name}` : ""}</span>` : nothing}
        <span class="range">
          @@ -${hunk.old_start},${hunk.old_lines} +${hunk.new_start},${hunk.new_lines} @@
        </span>
      </div>
      <div class="rows">
        <table
          class="diff"
          aria-label=${`Unified diff of ${hunk.label || "the file header"}, lines ${hunk.new_start} to ${hunk.new_start + hunk.new_lines}`}
        >
          <tbody>
            ${hunk.lines.map(
              // The two gutters are hidden from assistive technology: a reader
              // hearing "412 413 context" before every line learns nothing. The
              // mark is not hidden — added and removed is the meaning.
              (line) => html`<tr class=${line.op}>
                <td class="n" aria-hidden="true">${line.old || ""}</td>
                <td class="n" aria-hidden="true">${line.new || ""}</td>
                <td class="mark">
                  ${line.op === "added" ? "+" : line.op === "removed" ? "−" : " "}
                </td>
                <td class="text">${line.text}</td>
              </tr>`,
            )}
          </tbody>
        </table>
      </div>
    </div>`;
  }

  private renderFile(file: DiffFile) {
    const open = this.open[file.path] ?? false;
    return html`<div class="file">
      <div class="file-head">
        <bn-button
          size="sm"
          @click=${() => (this.open = { ...this.open, [file.path]: !open })}
          aria-expanded=${open ? "true" : "false"}
        >
          ${open ? "Hide" : "Show"}
        </bn-button>
        <span class="obj">${file.path}</span>
        <span class="meta">${file.change}</span>
        <span class="lines">
          <span class="plus">+${file.added}</span> <span class="minus">−${file.removed}</span>
        </span>
      </div>
      ${open ? file.hunks.map((h) => this.renderHunk(h)) : nothing}
    </div>`;
  }

  private renderFiles(diff: GenerationDiff) {
    const policyFiles = diff.files.filter((f) => f.path !== SERVER_CONFIG_PATH);
    const serverFile = diff.files.find((f) => f.path === SERVER_CONFIG_PATH);
    return html`
      ${policyFiles.length > 0
        ? html`<bn-card heading="Policy manifests">
            <p class="copy">
              Only the files that changed are listed. Each hunk carries the kind and name of the
              object it falls in, so a reader is never left working out which object a change belongs
              to.
            </p>
            ${policyFiles.map((f) => this.renderFile(f))}
          </bn-card>`
        : nothing}
      ${serverFile
        ? html`<bn-card heading="Rendered server configuration">
            <p class="copy">
              <strong>This section has a different blast radius.</strong> Nothing in
              <code>server.yaml</code> hot-reloads — not the limits, not the policy engine, not the
              DuckDB pool, not the audit configuration — so any change here replaces the running
              process. The text is redacted: <code>admin.token</code> is a fixed placeholder on both
              sides, because this view is readable by every member of the organization.
            </p>
            ${this.renderFile(serverFile)}
          </bn-card>`
        : nothing}
    `;
  }

  /**
   * The unchanged answer (`SC-S603`'s last criterion). An empty diff states "no
   * changes" plainly, and the publish action it offers is the one the API would
   * refuse — `draft_empty` — so the screen says it here instead.
   */
  private renderNoChanges() {
    return html`<bn-empty-state
      icon="="
      heading="No changes"
      message="These two render to the same bytes: the same manifests, the same server configuration, the same secret inventory and the same image. Publishing would be refused as draft_empty, because publishing twice must not cost an outage."
    >
      <bn-button
        slot="actions"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "generations"))}
      >
        Publish history
      </bn-button>
    </bn-empty-state>`;
  }

  /**
   * The failure state, including the one failure worth naming precisely: this build
   * may not serve the diff route yet. `06` §3.9 specifies it and `bundle.Diff`
   * computes the changeset, so the honest thing to say is which piece is missing and
   * where the same bytes can be read instead — the per-generation YAML views, which
   * are served.
   */
  private renderFailureCard(err: ApiError) {
    const unserved = err.status === 404 || err.code === "not_found";
    return html`<bn-card heading=${unserved ? "This deployment does not serve the diff yet" : "Could not load the diff"}>
      <div class="failure" role="alert">
        <p>${err.message}</p>
        ${unserved
          ? html`<p>
              <code>GET /v1/instances/{id}/diff</code> is specified and the renderer that computes the
              changeset exists; the HTTP route is not part of this build. Nothing is wrong with your
              configuration. Until it lands, the rendered YAML of each generation is served and is the
              same text both sides of this comparison are computed from.
            </p>`
          : nothing}
        <bn-kv .rows=${this.supportRows(err)}></bn-kv>
        <div class="actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
          <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "generations"))}>
            Publish history
          </bn-button>
          ${typeof this.to === "number"
            ? html`<bn-button
                @click=${() =>
                  navigate(hrefFor("instances", this.instanceId, "generations", this.to as number, "yaml"))}
              >
                YAML of generation ${this.to}
              </bn-button>`
            : nothing}
          <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}>
            Review the draft
          </bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  override render() {
    const diff = this.diff;
    const empty =
      diff !== null &&
      diff.files.length === 0 &&
      diff.lines_added === 0 &&
      diff.lines_removed === 0 &&
      !diff.admin_token_rotated;
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <h1>Diff</h1>
      <p class="lede">
        A unified, line-level comparison of the deterministically rendered bytes. It is meaningful
        precisely because the renderer is byte-stable: the same structured input always produces the
        same output, so every line here is a real change and not formatting drift.
      </p>
      ${this.renderPicker()}
      ${this.loading
        ? html`<bn-spinner block size="28" label="Computing the diff"></bn-spinner>`
        : this.failure
          ? this.renderFailureCard(this.failure)
          : diff
            ? html`<div class="stack">
                ${this.renderVerdict(diff)}
                ${empty ? this.renderNoChanges() : html`${this.renderSummary(diff)} ${this.renderFiles(diff)}`}
              </div>`
            : nothing}
    `;
  }
}

/** parseRef accepts the three spellings the route takes and nothing else, so a
 * malformed `?from=` becomes a default rather than a request the API answers
 * `422`. */
function parseRef(raw: string | null): DiffRef | null {
  if (!raw) return null;
  if (raw === "draft" || raw === "live") return raw;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

declare global {
  interface HTMLElementTagNameMap {
    "page-diff": PageDiff;
  }
}
